import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2 } from 'lucide-react'

import { AmountInput } from '@/components/AmountInput'
import { DateTimePicker } from '@/components/DateTimePicker'
import { SwitchField } from '@/components/SwitchField'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useNetwork } from '@/contexts/NetworkContext'
import {
  budgetFlight,
  calendarDayOf,
  isPeriodEditable,
  nextPeriod,
  relinkPeriods,
  shiftDay,
  today,
  type BudgetPeriod,
} from '@/services/lineItems/budgets'
import { formatCalendarDay, parseCalendarDay } from '@/utils/format'
import { formatCurrencyAmount } from '@/utils/money'

export interface BudgetPeriodsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  periods: BudgetPeriod[]
  rollover: boolean
  /** The one currency every period is spent in — the object's own. */
  currency: string
  /** The object's dates, which seed the first period when there is none yet. */
  flight?: { startDate?: string; endDate?: string }
  /** A single budget already set on the object, carried into the first period. */
  seedAmount?: number
  /** What the object is called, for the title — "Line item", "Order". */
  objectLabel: string
  onSave: (periods: BudgetPeriod[], rollover: boolean) => void
}

/**
 * Edits the periods a budget is split over. The periods are one unbroken
 * timeline: only the first one's start and each period's end can be moved, and
 * everything after a moved edge re-links behind it, because the backend takes
 * nothing else — periods must be contiguous, and the object's own dates must
 * be the first start and the last end.
 *
 * A period that has already ended is frozen: its amount is immutable and it
 * cannot be removed.
 */
export function BudgetPeriodsDialog({
  open,
  onOpenChange,
  periods,
  rollover,
  currency,
  flight,
  seedAmount,
  objectLabel,
  onSave,
}: BudgetPeriodsDialogProps) {
  const { t } = useTranslation()
  const { currentNetwork } = useNetwork()
  const timeZone = currentNetwork?.timeZone ?? 'UTC'
  const now = today(timeZone)

  const [draft, setDraft] = useState<BudgetPeriod[]>([])
  const [draftRollover, setDraftRollover] = useState(rollover)

  // Opening is what seeds the editor — from the periods there are, or from the
  // object's own dates and single budget when this is the first split.
  useEffect(() => {
    if (!open) return
    setDraftRollover(rollover)
    if (periods.length > 0) {
      setDraft(periods)
      return
    }
    const startDay = calendarDayOf(flight?.startDate, timeZone) || now
    const endDay = calendarDayOf(flight?.endDate, timeZone)
    // The seeded period must still be open, or it is frozen before it has an
    // amount and the budget can never be applied.
    const fallbackEnd = nextPeriod(
      { startDay, endDay: shiftDay(startDay > now ? startDay : now, -1, timeZone) },
      timeZone
    ).endDay
    setDraft([
      {
        startDay,
        endDay: endDay && endDay >= startDay && endDay >= now ? endDay : fallbackEnd,
        ...(seedAmount ? { amount: { amount: seedAmount, currency } } : {}),
      },
    ])
    // Seeded once per opening: later prop churn must not throw away edits.
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const update = (index: number, changes: Partial<BudgetPeriod>) =>
    setDraft((rows) =>
      relinkPeriods(
        rows.map((row, i) => (i === index ? { ...row, ...changes } : row)),
        timeZone
      )
    )

  const remove = (index: number) =>
    setDraft((rows) =>
      relinkPeriods(
        rows.filter((_, i) => i !== index),
        timeZone
      )
    )

  const add = () => setDraft((rows) => [...rows, nextPeriod(rows[rows.length - 1], timeZone)])

  // Every period needs an amount, and the budget as a whole has to be worth
  // running — which is exactly what the backend asks of it. Removing every
  // period is allowed too: it hands the budget back to the single figure.
  const amountsSet = draft.every(
    (p) => typeof p.amount?.amount === 'number' && p.amount.amount >= 0
  )
  const anyPositive = draft.some((p) => (p.amount?.amount ?? 0) > 0)
  const blockedBy =
    draft.length === 0
      ? periods.length > 0
        ? null
        : 'noPeriods'
      : !amountsSet
        ? 'amountRequired'
        : !anyPositive
          ? 'amountPositive'
          : null
  const canSave = blockedBy === null

  // Rollover is frozen as soon as a period has ended, the same as the amounts.
  const hasEndedPeriod = draft.some((p) => !isPeriodEditable(p, timeZone))

  const derived = budgetFlight(draft, timeZone)
  const movesDates =
    !!derived &&
    (calendarDayOf(flight?.startDate, timeZone) !== draft[0]?.startDay ||
      calendarDayOf(flight?.endDate, timeZone) !== draft[draft.length - 1]?.endDay)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('budgets.editTitle', { object: objectLabel })}</DialogTitle>
          <DialogDescription>{t('budgets.editDescription')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {draft.length === 0 && (
            <Alert>
              <AlertDescription>{t('budgets.noPeriods')}</AlertDescription>
            </Alert>
          )}

          {draft.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('common.startDate')}</TableHead>
                  <TableHead>{t('common.endDate')}</TableHead>
                  <TableHead>{t('budgets.amount')}</TableHead>
                  <TableHead className="text-right">{t('budgets.spend')}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {draft.map((period, index) => {
                  const editable = isPeriodEditable(period, timeZone)
                  return (
                    <TableRow key={index}>
                      <TableCell>
                        {/* Only the first period's start is a free date: every
                            other one is the day after the period before it. */}
                        {index === 0 ? (
                          <DateTimePicker
                            dateOnly
                            clearable={false}
                            value={period.startDay}
                            disabled={!editable}
                            onChange={(v) => update(index, { startDay: v })}
                          />
                        ) : (
                          <span className="text-(--ad-text-muted)">
                            {formatCalendarDay(period.startDay)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <DateTimePicker
                          dateOnly
                          clearable={false}
                          value={period.endDay}
                          disabled={!editable}
                          fromDate={
                            parseCalendarDay(period.startDay > now ? period.startDay : now) ??
                            undefined
                          }
                          onChange={(v) => update(index, { endDay: v })}
                        />
                      </TableCell>
                      <TableCell>
                        <AmountInput
                          value={period.amount?.amount?.toString() ?? ''}
                          placeholder=""
                          onChange={(v) =>
                            update(index, {
                              amount:
                                v === ''
                                  ? undefined
                                  : {
                                      amount: parseFloat(v),
                                      currency: period.amount?.currency || currency,
                                    },
                            })
                          }
                          unit={period.amount?.currency || currency}
                          disabled={!editable}
                        />
                        {draftRollover && period.adjustedAmount && (
                          <p className="mt-1 text-xs text-(--ad-text-muted)">
                            {t('budgets.adjustedTo', {
                              amount: formatCurrencyAmount(period.adjustedAmount, 2),
                            })}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrencyAmount(period.spend, 2)}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          disabled={!editable}
                          aria-label={t('budgets.removePeriod')}
                          onClick={() => remove(index)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}

          <div>
            <Button type="button" variant="outline" size="sm" onClick={add}>
              <Plus className="size-4" />
              {t('budgets.addPeriod')}
            </Button>
          </div>

          <SwitchField
            label={t('budgets.rollover')}
            description={t('budgets.rolloverExplainer')}
            tooltip={hasEndedPeriod ? t('budgets.rolloverLocked') : undefined}
            checked={draftRollover}
            disabled={hasEndedPeriod}
            onCheckedChange={setDraftRollover}
          />

          {movesDates && derived && (
            <Alert>
              <AlertDescription>
                {t('budgets.datesWillMove', {
                  object: objectLabel.toLowerCase(),
                  startDate: formatCalendarDay(draft[0].startDay),
                  endDate: formatCalendarDay(draft[draft.length - 1].endDay),
                })}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="sm:items-center">
          {blockedBy && blockedBy !== 'noPeriods' && (
            <p className="mr-auto text-sm text-(--ad-text-muted)">{t(`budgets.${blockedBy}`)}</p>
          )}
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!canSave}
            onClick={() => {
              onSave(draft, draftRollover)
              onOpenChange(false)
            }}
          >
            {t('common.apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
